export const YSTNJ_NIGHT_MODE_STORAGE_KEY = 'ystnj.night-mode';
export const YSTNJ_NIGHT_MODE_AUTH_STORAGE_PREFIX = `${YSTNJ_NIGHT_MODE_STORAGE_KEY}.user.`;
export const YSTNJ_NIGHT_MODE_QUERY_PARAM = '__ystnjNightMode';
const YSTNJ_NIGHT_MODE_ATTRIBUTE = 'data-ystnj-night-mode';

const readLocalStorageValue = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorageValue = (key: string, value: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures and keep the current in-memory state.
  }
};

const parseStoredNightModePreference = (value: string | null): boolean | null => {
  if (value === '1') {
    return true;
  }
  if (value === '0') {
    return false;
  }
  return null;
};

const parseNightModeProviderOverride = (value: string | null): boolean | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['1', 'true', 'dark', 'on', 'enabled'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'light', 'off', 'disabled'].includes(normalized)) {
    return false;
  }
  return null;
};

const normalizeNightModeUserKey = (userKey: string | null | undefined): string | null => {
  const normalized = String(userKey ?? '').trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

export const toYstnjNightModeUserStorageKey = (
  userKey: string | null | undefined,
): string | null => {
  const normalized = normalizeNightModeUserKey(userKey);
  return normalized ? `${YSTNJ_NIGHT_MODE_AUTH_STORAGE_PREFIX}${encodeURIComponent(normalized)}` : null;
};

export const readYstnjNightModePreference = (): boolean => (
  readLocalStorageValue(YSTNJ_NIGHT_MODE_STORAGE_KEY) === '1'
);

export const readYstnjNightModeProviderOverride = (
  search?: string | null,
): boolean | null => {
  const sourceSearch = search ?? (
    typeof window === 'undefined' ? null : window.location.search
  );
  if (!sourceSearch) {
    return null;
  }
  try {
    const params = new URLSearchParams(sourceSearch);
    return parseNightModeProviderOverride(params.get(YSTNJ_NIGHT_MODE_QUERY_PARAM));
  } catch {
    return null;
  }
};

export const readYstnjNightModeProviderPreference = (): boolean => (
  readYstnjNightModeProviderOverride() ?? readYstnjNightModePreference()
);

export const persistYstnjNightModePreference = (enabled: boolean): void => {
  writeLocalStorageValue(YSTNJ_NIGHT_MODE_STORAGE_KEY, enabled ? '1' : null);
};

export const readYstnjNightModePreferenceForUser = (
  userKey: string | null | undefined,
): boolean | null => {
  const storageKey = toYstnjNightModeUserStorageKey(userKey);
  return storageKey ? parseStoredNightModePreference(readLocalStorageValue(storageKey)) : null;
};

export const persistYstnjNightModePreferenceForUser = (
  userKey: string | null | undefined,
  enabled: boolean,
): void => {
  const storageKey = toYstnjNightModeUserStorageKey(userKey);
  if (!storageKey) {
    return;
  }
  writeLocalStorageValue(storageKey, enabled ? '1' : '0');
};

export const applyYstnjNightMode = (enabled: boolean): void => {
  if (typeof document === 'undefined') {
    return;
  }
  if (enabled) {
    document.documentElement.setAttribute(YSTNJ_NIGHT_MODE_ATTRIBUTE, 'true');
    return;
  }
  document.documentElement.removeAttribute(YSTNJ_NIGHT_MODE_ATTRIBUTE);
};

export const syncYstnjNightModeFromStorage = (): boolean => {
  const enabled = readYstnjNightModeProviderPreference();
  applyYstnjNightMode(enabled);
  return enabled;
};

export const syncYstnjNightModeFromProviderAuthority = syncYstnjNightModeFromStorage;
